/** How the basket's numbers are written.
 *
 * Shared by the summary and the legs table so one basket cannot show two
 * conventions — a rupee figure in the header beside a points figure in a
 * row would be read as a discrepancy in the position, not in the units.
 */

import type { SimPayoff } from "@/lib/simulatorApi";

export function money(value: number | null, basis: SimPayoff["basis"]): string {
  if (value === null) return "—";
  const n = value.toFixed(2);
  return basis === "rupees" ? `₹${n}` : `${n} pts`;
}

/** Green above water, red below, muted when there is nothing to price. */
export function pnlTone(value: number | null): string {
  if (value === null) return "text-ink-muted";
  return value >= 0 ? "text-success" : "text-danger";
}

export function signed(value: number | null): string {
  return value === null || value <= 0 ? "" : "+";
}

/** The entry cost as a trader reads it.
 *
 * `net_entry_cost` is signed by direction (+1 BUY / -1 SELL), so a positive
 * figure is money PAID OUT for the basket and a negative one money TAKEN IN.
 * The header shows the amount unsigned with this word beside it, because
 * "-25,035" on a credit spread reads as a loss at a glance.
 */
export function costWord(netEntryCost: number): string {
  if (netEntryCost > 0) return "debit";
  if (netEntryCost < 0) return "credit";
  return "";
}

/** How far a breakeven sits from spot, in percent — the distance the index
 * has to travel before the basket turns. null when the session has no spot
 * for that minute, which is a dash, never a 0%. */
export function breakevenPct(breakeven: number, spot: number | null): string | null {
  if (spot === null || spot === 0) return null;
  const pct = ((breakeven - spot) / spot) * 100;
  return `${pct >= 0 ? "+" : ""}${pct.toFixed(2)}%`;
}

/** ⚠️ THE API SENDS "11:00:00", THE CLOCK HOLDS "11:00".
 *
 * These are compared as STRINGS, and "11:00" < "11:00:00" is true — a
 * shorter string that is a prefix sorts first. Left alone, a leg entered
 * at exactly the minute on screen reads as not yet taken, which is the
 * one minute it is certainly live. Trimming to HH:MM is the whole fix,
 * and it has to happen on BOTH ends of the comparison.
 */
export function hhmm(at: string): string {
  return at.slice(0, 5);
}
